import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { api } from '../../api/client'
import { useLanguageStore } from '../../store/languageStore'

const H = ({ style, ...p }: React.HTMLAttributes<HTMLElement>) => (
  <span style={{ fontFamily: "'Space Grotesk', sans-serif", ...style }} {...p} />
)

type ExamType = 'vstep' | 'ielts_academic' | 'ielts_general'

const EXAMS: { code: ExamType; label: string; desc: string; icon: string }[] = [
  { code: 'vstep', label: 'VSTEP', desc: 'Chứng chỉ ngoại ngữ Việt Nam (A2–C1)', icon: 'verified' },
  { code: 'ielts_academic', label: 'IELTS Academic', desc: 'Du học, học thuật (Band 0–9)', icon: 'school' },
  { code: 'ielts_general', label: 'IELTS General', desc: 'Định cư, làm việc (Band 0–9)', icon: 'work' },
]

// Target options differ per exam — engine stays CEFR-based underneath (FR-029)
const VSTEP_LEVELS = [
  { code: 'A2', label: 'A2 — Sơ cấp', desc: 'Vốn tiếng Anh cơ bản', icon: 'school' },
  { code: 'B1', label: 'B1 — Trung cấp', desc: 'Giao tiếp hàng ngày', icon: 'auto_stories' },
  { code: 'B2', label: 'B2 — Cao cấp', desc: 'Làm việc chuyên nghiệp', icon: 'workspace_premium' },
  { code: 'C1', label: 'C1 — Thành thạo', desc: 'Học thuật & kinh doanh', icon: 'military_tech' },
]

const IELTS_LEVELS = [
  { code: '4.5', label: 'Band 4.5', desc: 'Cơ bản (~B1)', icon: 'school' },
  { code: '5.5', label: 'Band 5.5', desc: 'Trung cấp (~B2-)', icon: 'auto_stories' },
  { code: '6.5', label: 'Band 6.5', desc: 'Du học phổ biến (~B2)', icon: 'workspace_premium' },
  { code: '7.5', label: 'Band 7.5', desc: 'Thành thạo (~C1)', icon: 'military_tech' },
]

const TIMES = [5, 10, 15, 30]

const INTERESTS = [
  { id: 'công nghệ', icon: 'devices', label: 'Công nghệ' },
  { id: 'khoa học', icon: 'science', label: 'Khoa học' },
  { id: 'xã hội', icon: 'groups', label: 'Xã hội' },
  { id: 'kinh doanh', icon: 'business_center', label: 'Kinh doanh' },
  { id: 'văn hoá', icon: 'palette', label: 'Văn hoá' },
  { id: 'giáo dục', icon: 'local_library', label: 'Giáo dục' },
]

export default function OnboardingPage() {
  const navigate = useNavigate()
  const { setUiLanguage } = useLanguageStore()
  const [step, setStep] = useState<'goals' | 'language'>('goals')
  const [uiLanguage, setUiLanguageLocal] = useState<'vi' | 'en'>('vi')
  const [form, setForm] = useState({
    examType: 'vstep' as ExamType,
    targetLevel: '',
    examDate: '',
    dailyStudyMinutes: 15,
    interestTags: [] as string[],
  })

  const isIelts = form.examType !== 'vstep'
  const levels = isIelts ? IELTS_LEVELS : VSTEP_LEVELS

  const save = useMutation({
    mutationFn: () => api.put('/users/me/profile', {
      ...form,
      // Don't send empty string — zod enum/regex will reject it; let backend keep existing value
      targetLevel: form.targetLevel || undefined,
      examDate: form.examDate || undefined,
    }),
    onSuccess: () => setStep('language'),
  })

  // Language preference step
  if (step === 'language') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f6f6ff', fontFamily: "'Manrope', sans-serif", color: '#272e42', position: 'relative', overflow: 'hidden', padding: '2rem' }}>
        <div style={{ position: 'fixed', top: '-6rem', left: '-6rem', width: '24rem', height: '24rem', borderRadius: '50%', background: 'rgba(0,75,226,0.05)', filter: 'blur(7.5rem)', pointerEvents: 'none' }} />
        <div style={{ position: 'fixed', bottom: '-6rem', right: '-6rem', width: '24rem', height: '24rem', borderRadius: '50%', background: 'rgba(142,0,213,0.05)', filter: 'blur(7.5rem)', pointerEvents: 'none' }} />

        <div style={{ width: '100%', maxWidth: '42rem', position: 'relative', zIndex: 1 }}>
          {/* B2 suggestion banner */}
          {form.targetLevel === 'B2' || form.targetLevel === 'C1' ? (
            <div style={{ background: 'rgba(209,220,255,0.8)', backdropFilter: 'blur(24px)', borderRadius: '1.25rem', padding: '1.25rem 1.5rem', marginBottom: '3rem', display: 'flex', alignItems: 'center', gap: '1rem', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', right: 0, top: 0, opacity: 0.1, transform: 'translate(1rem, -1rem)' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '5rem', fontVariationSettings: "'FILL' 1" }}>stars</span>
              </div>
              <div style={{ width: '3.5rem', height: '3.5rem', borderRadius: '50%', background: '#004be2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span className="material-symbols-outlined" style={{ color: 'white', fontSize: '1.5rem', fontVariationSettings: "'FILL' 1" }}>military_tech</span>
              </div>
              <div style={{ flex: 1 }}>
                <H style={{ display: 'block', fontWeight: 800, fontSize: '1rem', color: '#001b61', marginBottom: '0.25rem' }}>Mục tiêu {form.targetLevel} — Thử thách cao!</H>
                <p style={{ fontSize: '0.875rem', color: '#535b71' }}>Chuyển giao diện sang tiếng Anh để tăng tốc độ tiếp thu ngôn ngữ.</p>
              </div>
              <button
                onClick={() => setUiLanguageLocal('en')}
                style={{ padding: '0.5rem 1rem', borderRadius: '9999px', background: 'rgba(0,75,226,0.1)', border: 'none', color: '#004be2', fontWeight: 700, fontSize: '0.8125rem', cursor: 'pointer', flexShrink: 0 }}
              >
                Chuyển ngay
              </button>
            </div>
          ) : null}

          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <H style={{ display: 'block', fontSize: 'clamp(2rem, 5vw, 3.5rem)', fontWeight: 900, letterSpacing: '-0.04em', lineHeight: 0.95, marginBottom: '1rem' }}>
              Ngôn ngữ của bạn,<br /><span style={{ color: '#004be2', fontStyle: 'italic' }}>nhịp độ của bạn.</span>
            </H>
            <p style={{ color: '#535b71', fontSize: '1.0625rem', lineHeight: 1.6 }}>
              Chọn ngôn ngữ giao diện để bắt đầu hành trình học tập cá nhân hoá.
            </p>
          </div>

          {/* Language selection */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '2.5rem' }}>
            {/* Vietnamese */}
            <button
              onClick={() => setUiLanguageLocal('vi')}
              style={{
                padding: '2rem', borderRadius: '1.5rem', border: 'none', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s',
                background: uiLanguage === 'vi' ? 'linear-gradient(135deg, #004be2, #809bff)' : '#eef0ff',
                boxShadow: uiLanguage === 'vi' ? '0 20px 50px rgba(0,75,226,0.15)' : 'none',
                transform: uiLanguage === 'vi' ? 'translateY(-2px)' : 'none',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4rem' }}>
                <div style={{ width: '3.5rem', height: '3.5rem', borderRadius: '0.875rem', background: uiLanguage === 'vi' ? 'rgba(255,255,255,0.2)' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span className="material-symbols-outlined" style={{ color: uiLanguage === 'vi' ? 'white' : '#004be2', fontSize: '1.75rem' }}>translate</span>
                </div>
                {uiLanguage === 'vi' && <span className="material-symbols-outlined" style={{ color: 'white', fontSize: '1.5rem', fontVariationSettings: "'FILL' 1" }}>check_circle</span>}
              </div>
              <H style={{ display: 'block', fontSize: '1.75rem', fontWeight: 800, color: uiLanguage === 'vi' ? 'white' : '#272e42', marginBottom: '0.5rem' }}>Tiếng Việt</H>
              <p style={{ fontSize: '0.875rem', color: uiLanguage === 'vi' ? 'rgba(255,255,255,0.8)' : '#535b71', fontWeight: 500 }}>Giao diện tiếng mẹ đẻ giúp bạn nắm bắt nền tảng vững chắc.</p>
            </button>

            {/* English */}
            <button
              onClick={() => setUiLanguageLocal('en')}
              style={{
                padding: '2rem', borderRadius: '1.5rem', border: uiLanguage === 'en' ? '2px solid #004be2' : '2px solid transparent', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s',
                background: uiLanguage === 'en' ? 'linear-gradient(135deg, #004be2, #809bff)' : 'white',
                boxShadow: uiLanguage === 'en' ? '0 20px 50px rgba(0,75,226,0.15)' : '0 4px 16px rgba(0,0,0,0.04)',
                transform: uiLanguage === 'en' ? 'translateY(-2px)' : 'none',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4rem' }}>
                <div style={{ width: '3.5rem', height: '3.5rem', borderRadius: '0.875rem', background: uiLanguage === 'en' ? 'rgba(255,255,255,0.2)' : '#eef0ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span className="material-symbols-outlined" style={{ color: uiLanguage === 'en' ? 'white' : '#004be2', fontSize: '1.75rem', fontVariationSettings: "'FILL' 1" }}>public</span>
                </div>
                {uiLanguage === 'en' && <span className="material-symbols-outlined" style={{ color: 'white', fontSize: '1.5rem', fontVariationSettings: "'FILL' 1" }}>check_circle</span>}
              </div>
              <H style={{ display: 'block', fontSize: '1.75rem', fontWeight: 800, color: uiLanguage === 'en' ? 'white' : '#272e42', marginBottom: '0.5rem' }}>English</H>
              <p style={{ fontSize: '0.875rem', color: uiLanguage === 'en' ? 'rgba(255,255,255,0.8)' : '#535b71', fontWeight: 500 }}>Full immersion. Best for users aiming for fluent, natural interaction.</p>
            </button>
          </div>

          {/* Progress dots */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', marginBottom: '2rem' }}>
            {[0, 1, 2, 3].map(i => (
              <div key={i} style={{ width: i === 2 ? '2.5rem' : '0.625rem', height: '0.625rem', borderRadius: '9999px', background: i === 2 ? '#004be2' : 'rgba(0,75,226,0.2)', boxShadow: i === 2 ? '0 0 10px rgba(0,75,226,0.4)' : 'none', transition: 'all 0.3s' }} />
            ))}
          </div>

          <button
            onClick={() => {
              setUiLanguage(uiLanguage)
              api.patch('/users/language', { language: uiLanguage }).catch(() => {})
              navigate('/diagnostic')
            }}
            style={{
              width: '100%', padding: '1.25rem', borderRadius: '9999px', border: 'none', cursor: 'pointer',
              background: 'linear-gradient(135deg, #004be2, #809bff)', color: '#f2f1ff',
              fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '1.0625rem',
              boxShadow: '0 8px 24px rgba(0,75,226,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem',
            }}
          >
            Tiếp tục đến kiểm tra đầu vào
            <span className="material-symbols-outlined" style={{ fontSize: '1.375rem' }}>arrow_forward</span>
          </button>
        </div>
      </div>
    )
  }

  function toggleTag(tag: string) {
    setForm(f => ({
      ...f,
      interestTags: f.interestTags.includes(tag)
        ? f.interestTags.filter(t => t !== tag)
        : [...f.interestTags, tag],
    }))
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', fontFamily: "'Manrope', sans-serif", background: '#f6f6ff', color: '#272e42', position: 'relative', overflow: 'hidden' }}>
      {/* Blobs */}
      <div style={{ position: 'fixed', top: '-10%', right: '-10%', width: '30rem', height: '30rem', borderRadius: '50%', background: 'rgba(0,75,226,0.05)', filter: 'blur(100px)', pointerEvents: 'none' }} />
      <div style={{ position: 'fixed', bottom: '-10%', left: '-10%', width: '30rem', height: '30rem', borderRadius: '50%', background: 'rgba(142,0,213,0.05)', filter: 'blur(100px)', pointerEvents: 'none' }} />

      <main style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(20rem, 1fr))', gap: '3rem', width: '100%', maxWidth: '72rem', margin: '0 auto', padding: '4rem 2rem', alignItems: 'start', position: 'relative', zIndex: 1 }}>
        {/* Left panel */}
        <div style={{ position: 'sticky', top: '2rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: '2.5rem' }}>
            <div style={{ width: '2.5rem', height: '2.5rem', borderRadius: '0.75rem', background: 'linear-gradient(135deg,#004be2,#809bff)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,75,226,0.25)' }}>
              <span className="material-symbols-outlined" style={{ color: 'white', fontSize: '1.375rem', fontVariationSettings: "'FILL' 1" }}>bolt</span>
            </div>
            <H style={{ fontWeight: 800, fontSize: '1.25rem', letterSpacing: '-0.03em', color: '#004be2' }}>EngHustle</H>
          </div>

          <H style={{ display: 'block', fontSize: 'clamp(2.5rem,5vw,4rem)', fontWeight: 900, letterSpacing: '-0.04em', lineHeight: 0.9, marginBottom: '1.5rem' }}>
            Level up<br /><span style={{ color: '#004be2' }}>VSTEP</span><br />Journey.
          </H>
          <p style={{ color: '#535b71', fontSize: '1.0625rem', lineHeight: 1.7, maxWidth: '22rem', marginBottom: '2.5rem' }}>
            Tuỳ chỉnh lộ trình học siêu tốc của bạn trong 4 bước nhanh. Mất khoảng 2–3 phút.
          </p>

          {/* Progress steps */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0', marginBottom: '2rem' }}>
            {['Mục tiêu', 'Thời gian', 'Kỳ thi', 'Sở thích'].map((s, i) => (
              <div key={s} style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.375rem' }}>
                  <div style={{ width: '2rem', height: '2rem', borderRadius: '50%', background: i <= 3 ? 'linear-gradient(135deg,#004be2,#809bff)' : '#d9e2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 800, fontSize: '0.75rem', boxShadow: i === 3 ? '0 0 12px rgba(142,0,213,0.4)' : 'none' }}>
                    {i + 1}
                  </div>
                  <span style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.05em', color: '#535b71', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{s}</span>
                </div>
                {i < 3 && <div style={{ width: '2rem', height: '2px', background: 'linear-gradient(90deg,#004be2,#809bff)', margin: '0 0.25rem', marginBottom: '1.125rem' }} />}
              </div>
            ))}
          </div>
        </div>

        {/* Right: Form */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          {/* Exam type (FR-029) */}
          <section style={{ background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(16px)', borderRadius: '1.5rem', padding: '2rem' }}>
            <H style={{ display: 'block', fontSize: '1.125rem', fontWeight: 700, marginBottom: '0.5rem', color: '#272e42' }}>Kỳ thi mục tiêu</H>
            <p style={{ fontSize: '0.875rem', color: '#535b71', marginBottom: '1.25rem' }}>Bạn đang luyện thi chứng chỉ nào?</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(8rem,1fr))', gap: '0.75rem' }}>
              {EXAMS.map(ex => {
                const sel = form.examType === ex.code
                return (
                  <button
                    key={ex.code}
                    // Switching exam resets target so the user re-picks a valid band/level
                    onClick={() => setForm(f => ({ ...f, examType: ex.code, targetLevel: '' }))}
                    style={{
                      padding: '1.25rem', borderRadius: '1rem', border: 'none', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s',
                      background: sel ? 'linear-gradient(135deg,#004be2,#809bff)' : '#eef0ff',
                      boxShadow: sel ? '0 8px 20px rgba(0,75,226,0.25)' : 'none',
                      transform: sel ? 'scale(1.02)' : 'none',
                    }}
                  >
                    <span className="material-symbols-outlined" style={{ color: sel ? 'rgba(255,255,255,0.85)' : '#004be2', fontSize: '1.5rem', display: 'block', marginBottom: '0.5rem', fontVariationSettings: sel ? "'FILL' 1" : "'FILL' 0" }}>{ex.icon}</span>
                    <H style={{ display: 'block', fontSize: '1rem', fontWeight: 800, color: sel ? 'white' : '#272e42', marginBottom: '0.125rem' }}>{ex.label}</H>
                    <span style={{ fontSize: '0.75rem', color: sel ? 'rgba(255,255,255,0.75)' : '#535b71' }}>{ex.desc}</span>
                  </button>
                )
              })}
            </div>
          </section>

          {/* Target level */}
          <section style={{ background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(16px)', borderRadius: '1.5rem', padding: '2rem' }}>
            <H style={{ display: 'block', fontSize: '1.125rem', fontWeight: 700, marginBottom: '0.5rem', color: '#272e42' }}>{isIelts ? 'Mục tiêu IELTS' : 'Mục tiêu VSTEP'}</H>
            <p style={{ fontSize: '0.875rem', color: '#535b71', marginBottom: '1.25rem' }}>Bạn muốn đạt {isIelts ? 'band' : 'cấp độ'} nào?</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '0.75rem' }}>
              {levels.map(l => {
                const sel = form.targetLevel === l.code
                return (
                  <button
                    key={l.code}
                    onClick={() => setForm(f => ({ ...f, targetLevel: l.code }))}
                    style={{
                      padding: '1.25rem', borderRadius: '1rem', border: 'none', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s',
                      background: sel ? 'linear-gradient(135deg,#004be2,#809bff)' : '#eef0ff',
                      boxShadow: sel ? '0 8px 20px rgba(0,75,226,0.25)' : 'none',
                      transform: sel ? 'scale(1.02)' : 'none',
                    }}
                  >
                    <span className="material-symbols-outlined" style={{ color: sel ? 'rgba(255,255,255,0.8)' : '#004be2', fontSize: '1.5rem', display: 'block', marginBottom: '0.5rem', fontVariationSettings: sel ? "'FILL' 1" : "'FILL' 0" }}>{l.icon}</span>
                    <H style={{ display: 'block', fontSize: '1rem', fontWeight: 800, color: sel ? 'white' : '#272e42', marginBottom: '0.125rem' }}>{l.label}</H>
                    <span style={{ fontSize: '0.75rem', color: sel ? 'rgba(255,255,255,0.7)' : '#535b71' }}>{l.desc}</span>
                  </button>
                )
              })}
            </div>
          </section>

          {/* Daily time */}
          <section style={{ background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(16px)', borderRadius: '1.5rem', padding: '2rem' }}>
            <H style={{ display: 'block', fontSize: '1.125rem', fontWeight: 700, marginBottom: '0.5rem', color: '#272e42' }}>Thời gian học mỗi ngày</H>
            <p style={{ fontSize: '0.875rem', color: '#535b71', marginBottom: '1.25rem' }}>Chọn mức phù hợp với lịch của bạn.</p>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              {TIMES.map(t => {
                const sel = form.dailyStudyMinutes === t
                return (
                  <button
                    key={t}
                    onClick={() => setForm(f => ({ ...f, dailyStudyMinutes: t }))}
                    style={{
                      flex: 1, minWidth: '5rem', padding: '1rem 0.5rem', borderRadius: '9999px', border: 'none', cursor: 'pointer', fontFamily: "'Space Grotesk',sans-serif", fontWeight: 800, fontSize: '0.9375rem', transition: 'all 0.2s',
                      background: sel ? 'linear-gradient(135deg,#004be2,#809bff)' : '#eef0ff',
                      color: sel ? 'white' : '#272e42',
                      boxShadow: sel ? '0 4px 12px rgba(0,75,226,0.25)' : 'none',
                    }}
                  >
                    {t} phút
                  </button>
                )
              })}
            </div>
          </section>

          {/* Exam date */}
          <section style={{ background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(16px)', borderRadius: '1.5rem', padding: '2rem' }}>
            <H style={{ display: 'block', fontSize: '1.125rem', fontWeight: 700, marginBottom: '0.5rem', color: '#272e42' }}>Ngày thi dự kiến</H>
            <p style={{ fontSize: '0.875rem', color: '#535b71', marginBottom: '1.25rem' }}>Không bắt buộc — AI sẽ tính toán kế hoạch cho bạn.</p>
            <input
              type="date"
              value={form.examDate}
              onChange={e => setForm(f => ({ ...f, examDate: e.target.value }))}
              style={{ width: '100%', padding: '1rem 1.25rem', borderRadius: '0.75rem', background: '#eef0ff', border: 'none', outline: 'none', fontFamily: "'Manrope',sans-serif", fontSize: '1rem', fontWeight: 500, color: '#272e42', transition: 'all 0.2s' }}
              onFocus={e => { e.target.style.background = '#f6f6ff'; e.target.style.boxShadow = '0 0 0 2px #004be2' }}
              onBlur={e => { e.target.style.background = '#eef0ff'; e.target.style.boxShadow = 'none' }}
            />
          </section>

          {/* Interests */}
          <section style={{ background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(16px)', borderRadius: '1.5rem', padding: '2rem' }}>
            <H style={{ display: 'block', fontSize: '1.125rem', fontWeight: 700, marginBottom: '0.5rem', color: '#272e42' }}>Chủ đề yêu thích</H>
            <p style={{ fontSize: '0.875rem', color: '#535b71', marginBottom: '1.25rem' }}>AI sẽ chọn nội dung học phù hợp với sở thích của bạn.</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '0.75rem' }}>
              {INTERESTS.map(int => {
                const sel = form.interestTags.includes(int.id)
                return (
                  <button
                    key={int.id}
                    onClick={() => toggleTag(int.id)}
                    style={{
                      padding: '1rem 0.75rem', borderRadius: '1rem', border: 'none', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem',
                      background: sel ? 'linear-gradient(135deg,rgba(0,75,226,0.1),rgba(128,155,255,0.1))' : '#eef0ff',
                      boxShadow: sel ? '0 0 0 2px #004be2' : 'none',
                    }}
                  >
                    <span className="material-symbols-outlined" style={{ color: sel ? '#004be2' : '#535b71', fontSize: '1.5rem', fontVariationSettings: sel ? "'FILL' 1" : "'FILL' 0" }}>{int.icon}</span>
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: sel ? '#004be2' : '#272e42' }}>{int.label}</span>
                  </button>
                )
              })}
            </div>
          </section>

          {/* Error */}
          {save.isError && (
            <div style={{ padding: '1rem', borderRadius: '0.75rem', background: 'rgba(180,19,64,0.08)', color: '#b41340', fontSize: '0.875rem', fontWeight: 600 }}>
              {(save.error as Error).message}
            </div>
          )}

          {/* Submit */}
          <button
            onClick={() => save.mutate()}
            disabled={save.isPending || !form.targetLevel}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem',
              padding: '1.25rem 2.5rem', borderRadius: '9999px', border: 'none',
              cursor: save.isPending || !form.targetLevel ? 'not-allowed' : 'pointer',
              background: save.isPending || !form.targetLevel ? 'rgba(0,75,226,0.4)' : 'linear-gradient(135deg,#004be2,#809bff)',
              color: '#f2f1ff', fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: '1.0625rem',
              boxShadow: '0 8px 24px rgba(0,75,226,0.2)', transition: 'all 0.2s',
            }}
            onMouseEnter={e => { if (!save.isPending && form.targetLevel) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,75,226,0.35)' } }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,75,226,0.2)' }}
          >
            {save.isPending ? (
              <><span className="spinner" style={{ width: '1.25rem', height: '1.25rem', borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }} /> Đang lưu...</>
            ) : !form.targetLevel ? (
              <>Hãy chọn mục tiêu {isIelts ? 'IELTS' : 'VSTEP'} để tiếp tục</>
            ) : (
              <>Tiếp tục → Kiểm tra đầu vào <span className="material-symbols-outlined" style={{ fontSize: '1.375rem' }}>arrow_forward</span></>
            )}
          </button>
        </div>
      </main>
    </div>
  )
}
